import { ParsedQuery } from 'query-string';

import { TableData, TableRow } from '@Components/Table';
import { CommonConstants } from '@Constants/index';
import { DncItems, DncList, DncFilters } from '@Models/dnc.models';
import {
  formatToUSNumber,
  getDateString,
  addMinutes,
} from '@Utils/common/common.utils';

import { GlobalDncRowData } from './GlobalDnc/GlobalDnc.types';

/**
 * Takes the passed parameters
 * Returns data for table row in desired format.
 */
export const createDncData = (
  phone: React.ReactNode,
  createDate: string,
  lastUpdateDate: string,
  reason: string,
  comments: string,
  actions?: React.ReactNode
): TableData => {
  const dateCreate = getDateString(createDate);
  const dateUpdate = getDateString(lastUpdateDate);

  return {
    phone,
    createDate: dateCreate,
    lastUpdateDate: dateUpdate,
    reason,
    comments,
    actions,
  };
};

/**
 * Returns Rows and Total Rows Count for Table Component.
 */
export const getDncRows = (
  items: DncItems[] = [],
  actions: (id: string, phone: string, disabled: boolean) => JSX.Element,
  modalBtnComponent: (
    id: string,
    phoneNumber: string,
    btnText: string,
    disabled: boolean
  ) => JSX.Element
): TableRow[] => {
  const rows: TableRow[] = items.map((data) => ({
    id: data.id.toString(),
    data: createDncData(
      modalBtnComponent(
        data.id.toString(),
        data.phone.toString(),
        formatToUSNumber(data.phone),
        data.deleted
      ),
      data.createDate || '',
      data.lastUpdateDate || '',
      data.reason,
      data.comments || '',
      actions(data.id.toString(), formatToUSNumber(data.phone), data.deleted)
    ),
    disabled: data.deleted,
  }));

  return rows;
};

/**
 * Takes the passed parameters
 * Returns data for table row in desired format for admin.
 */
export const createDataGlobalDnc = (
  phone: React.ReactNode,
  createDate: string,
  lastUpdateDate: string,
  reason: string,
  comments: string,
  actions?: React.ReactNode
): GlobalDncRowData => {
  const dateCreate = getDateString(createDate);
  const dateUpdate = getDateString(lastUpdateDate);

  return {
    phone,
    createDate: dateCreate,
    lastUpdateDate: dateUpdate,
    reason,
    comments,
    actions,
  };
};

/**
 * Returns Rows and Total Rows Count for Table Component.
 */
export const getGlobalDncRows = (
  items: DncItems[] = [],
  actions: (id: string, disabled: boolean, phoneNo: string) => JSX.Element,
  modalBtnComponent: (
    id: string,
    phoneNumber: string,
    btnText: string,
    disabled: boolean
  ) => JSX.Element
): TableRow[] => {
  const rows: TableRow[] = items.map((data) => ({
    id: data.id.toString(),
    data: createDataGlobalDnc(
      modalBtnComponent(
        data.id.toString(),
        data.phone.toString(),
        formatToUSNumber(data.phone),
        data.deleted
      ),
      data.createDate || '',
      data.lastUpdateDate || '',
      data.reason,
      data.comments || '',
      actions(data.id.toString(), data.deleted, formatToUSNumber(data.phone))
    ),
    disabled: data.deleted,
  }));

  return rows;
};

/** Handles Export Filters */
export const getExportFilters = (
  queries: DncList,
  checked: readonly string[],
  itemsCount?: number
): DncList => {
  if (itemsCount) {
    return {
      ...queries,
      itemsCount,
    };
  }

  const newFilters = queries.filters?.filter(
    (item) => item.operationType !== 'IN'
  );

  newFilters?.push({
    dateRangeValue: null,
    operationType: 'IN',
    propertyName: 'ID',
    value: checked.map((id) => parseInt(id, 10)),
  });

  return {
    ...queries,
    filters: newFilters,
  };
};

/** Set Date Filter with format
 * Update queries state.
 * Dispatch API getDncList
 */
export const setDateFilter = (start?: string, end?: string): DncFilters[] => {
  if (start && end) {
    /** Shifting to next day
     * Because API return data till (end - 1) date.
     */
    const shiftedEnd = addMinutes(new Date(end), 1440).toISOString();

    const startDate = new Date(start);
    const endDate = new Date(shiftedEnd);
    const from = `${startDate.getFullYear()}-${
      startDate.getMonth() + 1 < 10
        ? `0${startDate.getMonth() + 1}`
        : startDate.getMonth() + 1
    }-${
      startDate.getDate() < 10 ? `0${startDate.getDate()}` : startDate.getDate()
    }`;

    const to = `${endDate.getFullYear()}-${
      endDate.getMonth() + 1 < 10
        ? `0${endDate.getMonth() + 1}`
        : endDate.getMonth() + 1
    }-${endDate.getDate() < 10 ? `0${endDate.getDate()}` : endDate.getDate()}`;

    const newDateFilter = {
      dateRangeValue: {
        from,
        to,
      },
      operationType: 'DATE_RANGE',
      propertyName: CommonConstants.DNC_SORT.CREATE_DATE,
      value: {},
    };

    return [newDateFilter];
  }
  return [];
};

/** Set Account ID Filter --> */
export const setAccountIdFilter = (
  prevFilters: DncFilters[],
  accountId?: number
): DncFilters[] => {
  let newFilter: DncFilters;

  const newFilters = prevFilters.filter(
    (item) => item.propertyName !== 'ACCOUNT_ID'
  );

  if (accountId) {
    newFilter = {
      dateRangeValue: null,
      operationType: 'EQUALS',
      propertyName: 'ACCOUNT_ID',
      value: accountId,
    };
    newFilters.push(newFilter);
    return newFilters;
  }

  return prevFilters;
};
/** Set Account ID Filter <-- */

/** Return DncList from queryParams --> */
export const getDncListQuery = (
  dncQuery: ParsedQuery<string | number>
): DncList => {
  /** Add Filters -->
   * Add Date Filter
   */
  let localFilters: DncFilters[] = setDateFilter(
    (dncQuery[CommonConstants.QUERY_PARAMS.COMMON.DATE_START] as string) || '',
    (dncQuery[CommonConstants.QUERY_PARAMS.COMMON.DATE_END] as string) || ''
  );

  /** Add accountId filter */
  localFilters = setAccountIdFilter(
    localFilters,
    dncQuery[CommonConstants.QUERY_PARAMS.USERS.USER_ID] as number
  );
  /** <-- Add Filters */

  return {
    filters: localFilters,
    pagination: {
      pageNumber:
        (dncQuery[CommonConstants.QUERY_PARAMS.COMMON.PAGE_NO] as number) ||
        CommonConstants.INITIAL_PAGE_NO,
      numberPerPage:
        (dncQuery[CommonConstants.QUERY_PARAMS.COMMON.PAGE_SIZE] as number) ||
        CommonConstants.INITIAL_ROWS_PER_PAGE,
    },
    sort: {
      field:
        (dncQuery[CommonConstants.QUERY_PARAMS.COMMON.ORDER] as string) ||
        CommonConstants.INITIAL_DNC_SORT_ORDER,
      direction:
        (dncQuery[CommonConstants.QUERY_PARAMS.COMMON.DIRECTION] as string) ||
        CommonConstants.INITIAL_SORT_DIRECTION,
    },
    searchTerm:
      (dncQuery[CommonConstants.QUERY_PARAMS.DNC.PHONE] as string) || '',
    items: [],
    total: 0,
  };
};
/** Return DncList from queryParams <-- */
